import type { VitrinCourseDetailLesson } from "@/models/course/VitrinCourseDetail";

// Shared formatting helpers for the course landing page.

/** Format a number with Persian digits and grouping. */
export function fa(value: number): string {
  return Number(value || 0).toLocaleString("fa-IR");
}

/**
 * The API has no per-course duration field, so total hours is *estimated*
 * from the lesson count. Tweak this single constant to change the estimate.
 */
export const ESTIMATED_HOURS_PER_LESSON = 1.5;

export function estimatedHours(lessonsCount: number): number {
  return Math.max(0, Math.round(lessonsCount * ESTIMATED_HOURS_PER_LESSON));
}

/** Number of lessons that carry a quiz — used as the "آزمون" metric. */
export function countQuizzes(lessons: VitrinCourseDetailLesson[]): number {
  return lessons.reduce((sum, lesson) => sum + (lesson.has_quiz ? 1 : 0), 0);
}

type PriceInfo = {
  /** The price the user actually pays. */
  finalPrice: number;
  /** Original price, only when a discount applies. */
  originalPrice: number | null;
  /** Discount percentage (rounded), 0 when no discount. */
  discountPercent: number;
  /** Absolute saving in toman, 0 when no discount. */
  savings: number;
  isFree: boolean;
};

/**
 * Resolve the effective price from the API `price` / `special_price` pair.
 * `special_price` is `number | null`; a discount only counts when it is a
 * positive number strictly below `price`.
 */
export function resolvePrice(
  price: number,
  specialPrice: number | null | undefined,
): PriceInfo {
  const hasDiscount =
    typeof specialPrice === "number" &&
    specialPrice >= 0 &&
    price > 0 &&
    specialPrice < price;

  const finalPrice = hasDiscount ? (specialPrice as number) : price;

  return {
    finalPrice,
    originalPrice: hasDiscount ? price : null,
    discountPercent: hasDiscount
      ? Math.round((1 - (specialPrice as number) / price) * 100)
      : 0,
    savings: hasDiscount ? price - (specialPrice as number) : 0,
    isFree: finalPrice <= 0,
  };
}
